import React, { useState, useEffect } from "react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Label } from "../ui/label";
import { Paperclip, Trash, FileArrowUp, FilePdf, Image as ImageIcon, EnvelopeSimple, File, Eye, X } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import AdsCards from "../AdsCards";
import WhyThisMatters from "../WhyThisMatters";
import { api, API } from "../../lib/api";
import { toast } from "sonner";
import { useMarketplaceEnabled } from "../../context/ConfigContext";

const inputCls = "rounded-none border-slate-300 focus:border-slate-900 focus:ring-1 focus:ring-slate-900";

const categoryIcon = (cat) => {
  if (cat === "photo") return ImageIcon;
  if (cat === "correspondence") return EnvelopeSimple;
  if (cat === "document") return FilePdf;
  return File;
};

export default function EvidenceTab({ caseData, reload }) {
  const marketplaceEnabled = useMarketplaceEnabled();
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("document");
  const [linkedEventId, setLinkedEventId] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (!previewItem) {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
      return;
    }
    let cancelled = false;
    let createdUrl = null;
    (async () => {
      try {
        const r = await fetch(`${API}/files/${previewItem.storage_path}`, { credentials: "include" });
        if (!r.ok) throw new Error("preview failed");
        const blob = await r.blob();
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setPreviewUrl(createdUrl);
      } catch (e) {
        toast.error("Could not load preview");
        setPreviewItem(null);
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [previewItem]);

  const onUpload = async () => {
    if (!file) return toast.error("Pick a file to upload");
    if (!label.trim()) return toast.error("Add a label for the exhibit");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const params = new URLSearchParams({ label, description, category });
      if (linkedEventId) params.append("linked_event_id", linkedEventId);
      await api.post(`/cases/${caseData.case_id}/evidence?${params.toString()}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Evidence uploaded");
      setFile(null);
      setLabel("");
      setDescription("");
      setLinkedEventId("");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeEvidence = async (id) => {
    if (!window.confirm("Remove this exhibit?")) return;
    try {
      await api.delete(`/cases/${caseData.case_id}/evidence/${id}`);
      toast.success("Removed");
      reload();
    } catch (e) {
      toast.error("Remove failed");
    }
  };

  return (
    <div className="space-y-10">
      <div className="flex items-baseline justify-between">
        <div>
          <h2 className="font-serif-display text-3xl text-slate-900">Evidence Organizer</h2>
          <p className="text-slate-600 mt-1">Upload exhibits (PDFs, images, correspondence) and link each to a timeline event.</p>
        </div>
        <span className="label-tiny">Step 4 of 8</span>
      </div>

      {/* Upload form */}
      <div className="border border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <FileArrowUp weight="duotone" className="w-6 h-6 text-slate-700" />
          <h3 className="font-serif-display text-xl">Add Exhibit</h3>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="label-tiny">Label</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Exhibit A: Contract" className={inputCls} data-testid="evidence-label-input" />
          </div>
          <div className="space-y-2">
            <Label className="label-tiny">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className={inputCls} data-testid="evidence-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="document">Document</SelectItem>
                <SelectItem value="photo">Photo</SelectItem>
                <SelectItem value="correspondence">Correspondence</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="label-tiny">Linked Timeline Event (optional)</Label>
            <Select value={linkedEventId || "none"} onValueChange={(v) => setLinkedEventId(v === "none" ? "" : v)}>
              <SelectTrigger className={inputCls} data-testid="evidence-link-event"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {(caseData.timeline || []).map((ev) => (
                  <SelectItem key={ev.id} value={ev.id}>{ev.date} · {ev.title || "Untitled"}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="label-tiny">File</Label>
            <Input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className={inputCls} data-testid="evidence-file-input" />
          </div>
          <div className="md:col-span-2 space-y-2">
            <Label className="label-tiny">Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Brief description of this exhibit" className={`${inputCls} min-h-[80px]`} data-testid="evidence-description-input" />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={onUpload} disabled={uploading} data-testid="upload-evidence-btn" className="rounded-none bg-slate-900 hover:bg-slate-800 text-white">
            {uploading ? "Uploading…" : "Upload exhibit"}
          </Button>
        </div>
      </div>

      {/* Existing evidence list */}
      <div>
        <h3 className="font-serif-display text-xl mb-4">Exhibits ({(caseData.evidence || []).length})</h3>
        {(caseData.evidence || []).length === 0 ? (
          <div className="border border-dashed border-slate-300 p-10 text-center text-slate-500">No exhibits yet.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-0">
            {(caseData.evidence || []).map((ev, i) => {
              const Icon = categoryIcon(ev.category);
              const linkedEvent = (caseData.timeline || []).find((t) => t.id === ev.linked_event_id);
              const exhibitLetter = i < 26 ? String.fromCharCode(65 + i) : String.fromCharCode(65 + Math.floor(i / 26) - 1) + String.fromCharCode(65 + (i % 26));
              return (
                <div key={ev.id} className={`border border-slate-200 ${i % 2 === 1 ? "md:border-l-0" : ""} -mt-px md:mt-0 p-5`} data-testid={`evidence-card-${ev.id}`}>
                  <div className="flex items-start gap-3">
                    <Icon weight="duotone" className="w-7 h-7 text-slate-700 shrink-0 mt-1" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono-tech text-[11px] text-slate-900 font-semibold">EXHIBIT {exhibitLetter}</span>
                        <span className="label-tiny">· {ev.category}</span>
                      </div>
                      <div className="font-serif-display text-lg text-slate-900 leading-tight">{ev.label}</div>
                      <div className="text-sm text-slate-600 mt-1">{ev.description}</div>
                      <div className="text-xs text-slate-500 mt-2 tabular-nums">
                        {ev.original_filename} · {Math.round((ev.size || 0) / 1024)} KB
                      </div>
                      {linkedEvent && (
                        <div className="mt-2 text-xs text-slate-700 border-l-2 border-slate-300 pl-2">
                          Linked: <span className="font-medium">{linkedEvent.date} · {linkedEvent.title}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => setPreviewItem(ev)} className="hover:bg-slate-100 rounded-none" data-testid={`preview-evidence-${ev.id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => removeEvidence(ev.id)} className="text-red-700 hover:bg-red-50 rounded-none" data-testid={`remove-evidence-${ev.id}`}>
                        <Trash className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Preview dialog */}
      <Dialog open={!!previewItem} onOpenChange={(o) => !o && setPreviewItem(null)}>
        <DialogContent className="max-w-4xl rounded-none border-slate-300 p-0" data-testid="evidence-preview-dialog">
          <DialogHeader className="p-6 pb-2">
            <DialogTitle className="font-serif-display text-2xl">{previewItem?.label}</DialogTitle>
            <DialogDescription>{previewItem?.original_filename}</DialogDescription>
          </DialogHeader>
          <div className="px-6 pb-6">
            {previewUrl ? (
              (previewItem?.content_type || "").startsWith("image/") ? (
                <img src={previewUrl} alt={previewItem.label} className="max-h-[70vh] mx-auto border border-slate-200" />
              ) : (previewItem?.content_type || "").includes("pdf") ? (
                <iframe src={previewUrl} title={previewItem.label} className="w-full h-[70vh] border border-slate-200" />
              ) : (
                <div className="text-center py-10 text-slate-500">
                  Preview not available for this file type.{" "}
                  <a href={previewUrl} download={previewItem.original_filename} className="underline">Download</a>
                </div>
              )
            ) : (
              <div className="text-center py-10 label-tiny">Loading preview…</div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {marketplaceEnabled && <AdsCards placement="evidence" />}
      <WhyThisMatters step="evidence" />
    </div>
  );
}
